import { Activity } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GenieArtifactUsageSummary } from '@/services/tokenEconomyService';

interface Props {
  summary: GenieArtifactUsageSummary | null;
}

function pickColor(pct: number): string {
  if (pct >= 100) return 'hsl(0, 80%, 55%)';
  if (pct >= 90) return 'hsl(0, 75%, 55%)';
  if (pct >= 60) return 'hsl(35, 90%, 55%)';
  return 'hsl(140, 60%, 45%)';
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return new Intl.NumberFormat().format(n);
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  } catch {
    return iso;
  }
}

export function UsageIndicator({ summary }: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!summary) return null;

  const pct = Math.min(summary.percentage, 100);
  const color = pickColor(summary.percentage);
  const label = `${Math.round(summary.percentage)}%`;

  return (
    <div style={{ position: 'relative' }} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t('genieArtifact.usage.title', 'Consommation IA (mois en cours)')}
        aria-label={t('genieArtifact.usage.title', 'Consommation IA (mois en cours)')}
        style={{
          border: '1px solid hsl(var(--border))',
          background: 'transparent',
          borderRadius: 8,
          padding: '6px 9px',
          cursor: 'pointer',
          color: 'hsl(var(--muted-foreground))',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        <Activity size={13} style={{ color }} />
        <span style={{ color }}>{label}</span>
        <span
          style={{
            display: 'inline-block',
            width: 36,
            height: 4,
            borderRadius: 99,
            background: 'hsl(var(--border))',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <span
            style={{
              position: 'absolute',
              inset: 0,
              width: `${pct}%`,
              background: color,
              transition: 'width 200ms ease',
            }}
          />
        </span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            minWidth: 260,
            background: 'hsl(var(--popover))',
            color: 'hsl(var(--popover-foreground))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 10,
            padding: 12,
            zIndex: 1000,
            boxShadow: '0 12px 24px hsl(var(--background) / 0.6)',
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            {t('genieArtifact.usage.title', 'Consommation IA (mois en cours)')}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: 'hsl(var(--muted-foreground))' }}>
              {t('genieArtifact.usage.used', 'Utilisés')}
            </span>
            <span style={{ fontWeight: 600 }}>{formatCompact(summary.used)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ color: 'hsl(var(--muted-foreground))' }}>
              {t('genieArtifact.usage.quota', 'Quota mensuel')}
            </span>
            <span style={{ fontWeight: 600 }}>{formatCompact(summary.quota)}</span>
          </div>
          <div
            style={{
              height: 6,
              borderRadius: 99,
              background: 'hsl(var(--border))',
              position: 'relative',
              overflow: 'hidden',
              margin: '8px 0',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                width: `${pct}%`,
                background: color,
                transition: 'width 200ms ease',
              }}
            />
          </div>
          <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: 10 }}>
            {t('genieArtifact.usage.resetsOn', 'Remise à zéro le {{date}}', {
              date: formatDate(summary.period_end, i18n.language),
            })}
          </div>
          {summary.blocked && (
            <div
              style={{
                marginTop: 10,
                padding: '6px 8px',
                borderRadius: 6,
                background: 'hsla(0, 80%, 55%, 0.12)',
                color: 'hsl(0, 80%, 55%)',
                fontWeight: 600,
                fontSize: 11,
              }}
            >
              {t('genieArtifact.usage.blockedShort', 'Quota atteint — services IA suspendus')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
